/**
 * AI Scribe IndexedDB Module
 *
 * Manages audio chunk storage in IndexedDB for recording persistence.
 * Chunks are written incrementally during recording (~1 second intervals)
 * and survive browser crashes, tab closes, and power loss.
 *
 * Uses a persistent connection singleton to avoid opening/closing the
 * database on every write (which would thrash IDB during recording).
 *
 * No external dependencies — uses the raw IndexedDB API.
 * All functions guard for SSR with typeof window checks.
 */

import type { StoredAudioChunk, StoredRecordingMeta } from './types';

const DB_NAME = 'ai-scribe-recordings';
const STORE_NAME = 'audio-chunks';
const META_STORE_NAME = 'recording-meta';
// LF-3743: bumped from 2 to 3 to add `handoffSource` and `uploadedAt` on the
// meta store. Both fields are additive on the existing keypath store so the
// onupgradeneeded step at v3 is a no-op — IDB tolerates absent fields on
// existing rows.
const DB_VERSION = 3;

/** Default max age for chunk cleanup (48 hours) */
const DEFAULT_MAX_AGE_MS = 48 * 60 * 60 * 1000;

// ─── Persistent Connection ─────────────────────────────────────────

let dbInstance: IDBDatabase | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Get a persistent database connection. Opens once, reuses thereafter.
 * Automatically reopens if the connection is closed (e.g. by browser GC).
 */
function getDb(): Promise<IDBDatabase> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available in SSR'));
  }

  if (dbInstance) return Promise.resolve(dbInstance);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = event.oldVersion;

      if (oldVersion < 1) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: ['recordingId', 'chunkIndex']
        });
        store.createIndex('byRecordingId', 'recordingId', { unique: false });
        store.createIndex('byCreatedAt', 'createdAt', { unique: false });
      }

      if (oldVersion < 2) {
        db.createObjectStore(META_STORE_NAME, { keyPath: 'recordingId' });
      }

      // LF-3743: v3 adds `handoffSource` and `uploadedAt` to StoredRecordingMeta.
      // Both fields are additive on an existing keypath store — no migration
      // required. The version bump exists to make the FE's IDB upgrade path
      // explicit and to keep tooling honest if/when a future store change
      // needs a real migration step here.
      if (oldVersion < 3) {
        // intentionally empty — additive fields only
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;

      // Reopen if the browser closes the connection
      dbInstance.onclose = () => {
        dbInstance = null;
        dbPromise = null;
      };

      resolve(dbInstance);
    };

    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });

  return dbPromise;
}

/**
 * Open (or create) the IndexedDB database.
 * @deprecated Use getDb() internally. Kept for external callers during migration.
 */
export function openDatabase(): Promise<IDBDatabase> {
  return getDb();
}

// ─── Write ──────────────────────────────────────────────────────────

/**
 * Write a single audio chunk to IndexedDB.
 * Called every ~1 second during recording. Uses the persistent connection.
 */
export async function writeChunk(chunk: StoredAudioChunk): Promise<void> {
  if (typeof window === 'undefined') return;

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(chunk);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Read ───────────────────────────────────────────────────────────

/**
 * Get all chunks for a recording, ordered by chunkIndex.
 */
export async function getChunksForRecording(
  recordingId: number
): Promise<StoredAudioChunk[]> {
  if (typeof window === 'undefined') return [];

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.objectStore(STORE_NAME).index('byRecordingId');
    const request = index.getAll(recordingId);

    request.onsuccess = () => {
      const chunks = (request.result as StoredAudioChunk[]).sort(
        (a, b) => a.chunkIndex - b.chunkIndex
      );
      resolve(chunks);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all orphaned chunks grouped by recordingId.
 * Used by the recovery store to detect crashed sessions.
 */
export async function getOrphanedChunks(): Promise<
  Map<number, StoredAudioChunk[]>
> {
  if (typeof window === 'undefined') return new Map();

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();

    request.onsuccess = () => {
      const allChunks = request.result as StoredAudioChunk[];
      const grouped = new Map<number, StoredAudioChunk[]>();

      for (const chunk of allChunks) {
        const existing = grouped.get(chunk.recordingId) ?? [];
        existing.push(chunk);
        grouped.set(chunk.recordingId, existing);
      }

      grouped.forEach((chunks, id) => {
        grouped.set(
          id,
          chunks.sort((a, b) => a.chunkIndex - b.chunkIndex)
        );
      });

      resolve(grouped);
    };
    request.onerror = () => reject(request.error);
  });
}

// ─── Delete ─────────────────────────────────────────────────────────

/**
 * Delete all chunks for a specific recording.
 * Called after successful upload or explicit discard.
 */
export async function deleteChunksForRecording(
  recordingId: number
): Promise<void> {
  if (typeof window === 'undefined') return;

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.objectStore(STORE_NAME).index('byRecordingId');
    const request = index.openCursor(recordingId);

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Delete all chunks older than maxAgeMs (default 48 hours).
 * Called on app load to clean up expired orphaned data.
 * Returns the recordingIds that were cleaned up.
 */
export async function cleanupExpiredChunks(
  maxAgeMs: number = DEFAULT_MAX_AGE_MS
): Promise<number[]> {
  if (typeof window === 'undefined') return [];

  const cutoff = Date.now() - maxAgeMs;
  const db = await getDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.objectStore(STORE_NAME).index('byCreatedAt');
    const range = IDBKeyRange.upperBound(cutoff);
    const request = index.openCursor(range);
    const deletedIds = new Set<number>();

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const chunk = cursor.value as StoredAudioChunk;
        deletedIds.add(chunk.recordingId);
        cursor.delete();
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve(Array.from(deletedIds));
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Recording Meta ────────────────────────────────────────────────

/**
 * Save or update recording metadata (timestamps, server recording ID, etc.).
 * Called when recording starts and again when the server recording is created.
 */
export async function writeRecordingMeta(
  meta: StoredRecordingMeta
): Promise<void> {
  if (typeof window === 'undefined') return;

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE_NAME, 'readwrite');
    tx.objectStore(META_STORE_NAME).put(meta);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get recording metadata by local ID.
 */
export async function getRecordingMeta(
  recordingId: number
): Promise<StoredRecordingMeta | null> {
  if (typeof window === 'undefined') return null;

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE_NAME, 'readonly');
    const request = tx.objectStore(META_STORE_NAME).get(recordingId);

    request.onsuccess = () =>
      resolve((request.result as StoredRecordingMeta) ?? null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete recording metadata by local ID.
 * Called after successful upload or explicit discard.
 */
export async function deleteRecordingMeta(recordingId: number): Promise<void> {
  if (typeof window === 'undefined') return;

  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE_NAME, 'readwrite');
    tx.objectStore(META_STORE_NAME).delete(recordingId);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
